import {isUndefined} from 'lodash';
import React, {FC} from 'react';

import {
  ClickEventHandler,
  FrontClickEvent,
} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {MessageTypesEnum} from '../../../../libs/shared/core/src/models/api/commonMessageModel';
import {CellsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {BlockLink} from '../../../../libs/shared/ui/src/components/links/blockLink';
import {LegacyInteractionParent} from '../../../../libs/shared/ui/src/interactionTracking/interactionParent';
import {TeammatesMessageFoldersEnum} from '../../../models/url/models/inboxesUrlModel';
import {InboxesTeammateMessagesParameters} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {CellBase} from './cellBase';
import {CompactMessageCellContent} from './cellContent/compactMessageCellContent';
import {MessageCellContent} from './cellContent/messageCellContent';
import {CompactCellBase} from './compactCellBase';
import {useCompactP2Context} from './compactP2Context';
import {MessageCellCommonProps} from './messageCellCommon';
import {MessageCellDragSourceContainer} from './messageCellDragSourceContainer';

/*
 * Props.
 */

export type MessageCellClickEventHandler = (messageUid: string, event: FrontClickEvent<HTMLElement>) => void;
export interface MessageCellProps extends MessageCellCommonProps {
  panelId: CellsPanelId;
  parameters: InboxesTeammateMessagesParameters;
  path?: string;
  onClick?: MessageCellClickEventHandler;
  onDoubleClick?: () => void;
  isLast?: boolean;
  isFirst?: boolean;
}

/*
 * Component.
 */

export const MessageCell: FC<MessageCellProps> = (props) => {
  const {message, path, onDoubleClick} = props;
  const {uid} = message;

  const onClick: ClickEventHandler = (event) => {
    if (!props.onClick) {
      return;
    }

    props.onClick(uid, event);
  };
  const {isCompact} = useCompactP2Context();

  const renderCell = () => {
    if (isCompact) {
      return (
        <CompactCellBase isFirst={props.isFirst} isLast={props.isLast} isSelected={props.isSelected}>
          <CompactMessageCellContent
            date={props.date}
            reminderDate={props.reminderDate}
            isSelected={props.isSelected}
            message={props.message}
            recipients={props.recipients}
            conversationSubject={props.conversationSubject}
            externalChannelType={props.externalChannelType}
            applicationChannelType={props.applicationChannelType}
            draftComment={props.draftComment}
            draft={props.draft}
            channelTypes={props.channelTypes}
            isInDraftsFolder={props.parameters.folder === TeammatesMessageFoldersEnum.DRAFTS}
            teammateId={props.parameters.teammateId}
          />
        </CompactCellBase>
      );
    }

    return (
      <CellBase isSelected={props.isSelected}>
        <MessageCellContent
          date={props.date}
          reminderDate={props.reminderDate}
          isSelected={props.isSelected}
          message={props.message}
          recipients={props.recipients}
          conversationSubject={props.conversationSubject}
          externalChannelType={props.externalChannelType}
          applicationChannelType={props.applicationChannelType}
          draftComment={props.draftComment}
          draft={props.draft}
          channelTypes={props.channelTypes}
          isInDraftsFolder={props.parameters.folder === TeammatesMessageFoldersEnum.DRAFTS}
          teammateId={props.parameters.teammateId}
        />
      </CellBase>
    );
  };

  // TODO: Use role=option with aria-selected instead of a button?
  const clickableCell = isUndefined(path) ? (
    <ChromelessButton onClick={onClick} onDoubleClick={onDoubleClick}>
      {renderCell()}
    </ChromelessButton>
  ) : (
    <BlockLink to={path} onClick={onClick} onDoubleClick={onDoubleClick}>
      {renderCell()}
    </BlockLink>
  );

  if (message.type !== MessageTypesEnum.EMAIL) {
    return clickableCell;
  }

  return (
    <LegacyInteractionParent interactionId={`message-cell-${message.uid}`}>
      <MessageCellDragSourceContainer
        panelId={props.panelId}
        parameters={props.parameters}
        isSelected={props.isSelected}
        message={message}
      >
        {clickableCell}
      </MessageCellDragSourceContainer>
    </LegacyInteractionParent>
  );
};
